import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import React, { ReactNode, useRef } from "react";

export type InfoTooltipProps = {
  children: ReactNode;
};
export const InfoTooltip = ({ children }: InfoTooltipProps): React.ReactElement => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const timeOutRef = useRef<NodeJS.Timeout>();
  const timeoutDuration = 120;

  const handleEnter = (isOpen: boolean) => {
    clearTimeout(timeOutRef.current);
    !isOpen && triggerRef.current?.click();
  };

  const handleLeave = (isOpen: boolean) => {
    timeOutRef.current = setTimeout(() => {
      isOpen && document.activeElement === triggerRef.current && triggerRef.current?.click();
    }, timeoutDuration);
  };

  return (
    <Popover className="relative inline-block pl-[2px] top-[4px]">
      {({ open }: { open: boolean }) => (
        <span
          className="relative inline-block"
          onMouseEnter={() => handleEnter(open)}
          onMouseLeave={() => handleLeave(open)}>
          <PopoverButton className="outline-none" ref={triggerRef}>
            <svg width="20" height="22" viewBox="0 0 16 17" fill="none" className="cursor-pointer">
              <mask
                id="mask0_3178_5440"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="16"
                height="17">
                <rect y="0.5" width="16" height="16" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_3178_5440)">
                <path
                  d="M7.99984 12.2501C8.18873 12.2501 8.34717 12.1834 8.47517 12.0501C8.60273 11.9167 8.6665 11.7556 8.6665 11.5667C8.6665 11.3779 8.60273 11.2167 8.47517 11.0834C8.34717 10.9501 8.18873 10.8834 7.99984 10.8834C7.79984 10.8834 7.63606 10.9501 7.5085 11.0834C7.3805 11.2167 7.3165 11.3779 7.3165 11.5667C7.3165 11.7556 7.3805 11.9167 7.5085 12.0501C7.63606 12.1834 7.79984 12.2501 7.99984 12.2501ZM7.5165 9.85008H8.4665C8.4665 9.52786 8.51095 9.26941 8.59984 9.07475C8.68873 8.88053 8.89984 8.61675 9.23317 8.28341C9.53317 7.99453 9.76095 7.72497 9.9165 7.47475C10.0721 7.22497 10.1498 6.93341 10.1498 6.60008C10.1498 6.0223 9.94428 5.5723 9.53317 5.25008C9.12206 4.92786 8.63317 4.76675 8.0665 4.76675C7.51095 4.76675 7.04984 4.91675 6.68317 5.21675C6.3165 5.51675 6.05539 5.86675 5.89984 6.26675L6.7665 6.60008C6.84428 6.38897 6.9805 6.17786 7.17517 5.96675C7.36939 5.75564 7.66095 5.65008 8.04984 5.65008C8.44984 5.65008 8.74428 5.7583 8.93317 5.97475C9.12206 6.19164 9.2165 6.42786 9.2165 6.68341C9.2165 6.90564 9.14984 7.11119 9.0165 7.30008C8.88317 7.48897 8.7165 7.6723 8.5165 7.85008C8.08317 8.23897 7.80828 8.55297 7.69184 8.79208C7.57495 9.03075 7.5165 9.38341 7.5165 9.85008ZM7.99984 14.8334C7.12206 14.8334 6.29717 14.6667 5.52517 14.3334C4.75273 14.0001 4.08317 13.5501 3.5165 12.9834C2.94984 12.4167 2.49984 11.7472 2.1665 10.9747C1.83317 10.2027 1.6665 9.37786 1.6665 8.50008C1.6665 7.6223 1.83317 6.79719 2.1665 6.02475C2.49984 5.25275 2.94984 4.58341 3.5165 4.01675C4.08317 3.45008 4.75273 3.00008 5.52517 2.66675C6.29717 2.33341 7.12206 2.16675 7.99984 2.16675C8.87761 2.16675 9.70273 2.33341 10.4752 2.66675C11.2472 3.00008 11.9165 3.45008 12.4832 4.01675C13.0498 4.58341 13.4998 5.25275 13.8332 6.02475C14.1665 6.79719 14.3332 7.6223 14.3332 8.50008C14.3332 9.37786 14.1665 10.2027 13.8332 10.9747C13.4998 11.7472 13.0498 12.4167 12.4832 12.9834C11.9165 13.5501 11.2472 14.0001 10.4752 14.3334C9.70273 14.6667 8.87761 14.8334 7.99984 14.8334ZM7.99984 13.8334C9.48873 13.8334 10.7498 13.3167 11.7832 12.2834C12.8165 11.2501 13.3332 9.98897 13.3332 8.50008C13.3332 7.01119 12.8165 5.75008 11.7832 4.71675C10.7498 3.68341 9.48873 3.16675 7.99984 3.16675C6.51095 3.16675 5.24984 3.68341 4.2165 4.71675C3.18317 5.75008 2.6665 7.01119 2.6665 8.50008C2.6665 9.98897 3.18317 11.2501 4.2165 12.2834C5.24984 13.3167 6.51095 13.8334 7.99984 13.8334Z"
                  fill="currentColor"
                />
              </g>
            </svg>
          </PopoverButton>
          <PopoverPanel anchor="bottom" className="flex flex-col">
            <span className="ml-2 px-2 md:px-4 py-2 md:py-3 border border-[#efefef] bg-white rounded-md shadow-md text-grey-60 text-[0.7rem] leading-4 md:text-xs font-normal min-w-[180px] md:min-w-[240px] max-w-[300px] top-full mt-1 z-20 text-left">
              {children}
            </span>
          </PopoverPanel>
        </span>
      )}
    </Popover>
  );
};
